// Router config file: exports route tree alongside helper components.
// Fast-refresh rule is not applicable to routing config.

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { LoadingScreen } from '@/components/loading-screen.js';

import { AppShell } from './app-shell.js';
import { AuthGuard } from './auth-guard.js';

const LoginPage = lazy(() =>
  import('@/features/auth/components/login-page.js').then((m) => ({ default: m.LoginPage })),
);
const ResetPage = lazy(() =>
  import('@/features/auth/components/reset-page.js').then((m) => ({ default: m.ResetPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/components/forgot-password-page.js').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/components/dashboard-page.js').then((m) => ({
    default: m.DashboardPage,
  })),
);
const TourenPage = lazy(() =>
  import('@/features/touren/components/touren-page.js').then((m) => ({ default: m.TourenPage })),
);
const FahrerPage = lazy(() =>
  import('@/features/fahrer/components/fahrer-page.js').then((m) => ({ default: m.FahrerPage })),
);
const FinanzenPage = lazy(() =>
  import('@/features/finanzen/components/finanzen-page.js').then((m) => ({
    default: m.FinanzenPage,
  })),
);
const FahrerPortalPage = lazy(() =>
  import('@/features/fahrer-portal/components/fahrer-portal-page.js').then((m) => ({
    default: m.FahrerPortalPage,
  })),
);
const LoginInfoPage = lazy(() =>
  import('@/features/login-info/components/login-info-page.js').then((m) => ({
    default: m.LoginInfoPage,
  })),
);
const PortalVerfuegbarkeitPage = lazy(() =>
  import('@/features/fahrer-portal/components/portal-verfuegbarkeit-page.js').then((m) => ({
    default: m.PortalVerfuegbarkeitPage,
  })),
);
const BestaetigungPage = lazy(() =>
  import('@/features/bestaetigung/components/bestaetigung-page.js').then((m) => ({
    default: m.BestaetigungPage,
  })),
);

function withSuspense(node: JSX.Element): JSX.Element {
  return <Suspense fallback={<LoadingScreen />}>{node}</Suspense>;
}

export const router = createBrowserRouter(
  [
    { path: '/login', element: withSuspense(<LoginPage />) },
    { path: '/fahrer/login', element: withSuspense(<LoginPage />) },
    { path: '/reset/:token', element: withSuspense(<ResetPage />) },
    { path: '/passwort-vergessen', element: withSuspense(<ForgotPasswordPage />) },
    { path: '/bestaetigung/:token', element: withSuspense(<BestaetigungPage />) },
    {
      element: (
        <AuthGuard allow={['disponent']}>
          <AppShell />
        </AuthGuard>
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" replace /> },
        { path: '/dashboard', element: withSuspense(<DashboardPage />) },
        { path: '/touren', element: withSuspense(<TourenPage />) },
        { path: '/fahrer', element: withSuspense(<FahrerPage />) },
        { path: '/finanzen', element: withSuspense(<FinanzenPage />) },
        { path: '/login-info', element: withSuspense(<LoginInfoPage />) },
      ],
    },
    {
      element: (
        <AuthGuard allow={['fahrer']}>
          <AppShell />
        </AuthGuard>
      ),
      children: [
        { path: '/portal', element: withSuspense(<FahrerPortalPage />) },
        {
          path: '/portal/verfuegbarkeit',
          element: withSuspense(<PortalVerfuegbarkeitPage />),
        },
      ],
    },
    { path: '*', element: <Navigate to="/login" replace /> },
  ],
  { basename: '/fahrtenplaner' },
);
